import React from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiUsers, FiHeart, FiGlobe } from 'react-icons/fi';

// Afbeeldingen importeren
import boekenmarktImage from '../assets/images/boekenmarkt.jpg';
import lionsMeetingImage from '../assets/images/varenmetfrans.jpg';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

interface TestimonialProps {
  quote: string;
  name: string;
  role: string;
  image: string;
}

const Testimonial: React.FC<TestimonialProps> = ({ quote, name, role, image }) => (
  <motion.div 
    variants={fadeIn}
    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg"
  >
    <div className="flex items-center mb-4">
      {/* <img 
        src={image} 
        alt={name} 
        className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-lions-blue dark:border-lions-yellow"
      /> */}
      <div>
        <h4 className="font-bold text-lg text-lions-blue dark:text-lions-yellow">{name}</h4>
        <p className="text-gray-600 dark:text-gray-400">{role}</p>
      </div>
    </div>
    <p className="text-gray-700 dark:text-gray-300 italic">"{quote}"</p>
  </motion.div>
);

const Membership: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-lions-dark text-white py-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/membership-hero.jpg" 
            alt="Word lid van Lions Club Hoorn De Doelen" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.h1 
              variants={fadeIn}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Word <span className="text-lions-yellow">Lid</span>
            </motion.h1>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Samen maken we een verschil in onze gemeenschap en wereldwijd. Ontdek hoe u deel kunt worden van Lions Club Hoorn De Doelen.
            </motion.p>
            <motion.div variants={fadeIn}>
              <a 
                href="#aanmelden" 
                className="btn-primary mr-4"
              >
                Interesse? Meld u aan
              </a>
              <a 
                href="#meer-info" 
                className="btn-secondary"
              >
                Meer informatie
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About Membership Section */}
      <section id="meer-info" className="py-16 bg-white dark:bg-lions-dark">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeIn}>
              <h2 className="text-3xl font-bold mb-6 text-lions-blue dark:text-lions-yellow">
                Wat betekent het om een Lion te zijn?
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Lions zijn mannen en vrouwen die hun vrije tijd besteden aan het helpen van anderen. 
                Als lid van Lions Club Hoorn De Doelen maakt u deel uit van Lions Clubs International, 
                de grootste serviceclub ter wereld met meer dan 1,4 miljoen leden in 200 landen.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Onze club bestaat uit betrokken mannen uit Hoorn en omgeving die zich inzetten voor 
                lokale en internationale goede doelen. We organiseren diverse activiteiten en 
                fondsenwerving om onze gemeenschap te ondersteunen.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Ons motto is "We Serve" - wij dienen. Dit komt tot uiting in alles wat we doen.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="relative w-1/2 mx-auto">
              <img 
                src={lionsMeetingImage} 
                alt="Lions Club bijeenkomst varen met Frans" 
                className="rounded-lg shadow-lg w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-lions-blue dark:bg-lions-yellow text-white dark:text-lions-dark p-4 rounded-lg shadow-lg">
                <p className="font-bold text-xl">Sinds 2005</p>
                <p>Actief in Hoorn</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-lions-light dark:bg-gray-900">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl font-bold mb-4 text-lions-blue dark:text-lions-yellow"
            >
              Voordelen van Lidmaatschap
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto"
            >
              Als lid van Lions Club Hoorn De Doelen geniet u van diverse voordelen en mogelijkheden.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div 
              variants={fadeIn}
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg text-center"
            >
              <div className="bg-lions-blue dark:bg-lions-yellow text-white dark:text-lions-dark rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FiUsers className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-lions-blue dark:text-lions-yellow">Netwerk</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Ontmoet gelijkgestemde professionals en bouw een waardevol netwerk op binnen en buiten de regio Hoorn.
              </p>
            </motion.div>

            <motion.div 
              variants={fadeIn}
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg text-center"
            >
              <div className="bg-lions-blue dark:bg-lions-yellow text-white dark:text-lions-dark rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FiHeart className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-lions-blue dark:text-lions-yellow">Maatschappelijke Impact</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Maak direct verschil in uw gemeenschap door deel te nemen aan betekenisvolle projecten en initiatieven.
              </p>
            </motion.div>

            <motion.div 
              variants={fadeIn}
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg text-center"
            >
              <div className="bg-lions-blue dark:bg-lions-yellow text-white dark:text-lions-dark rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FiGlobe className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-lions-blue dark:text-lions-yellow">Internationale Verbinding</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Maak deel uit van een wereldwijde organisatie en draag bij aan internationale hulpprojecten.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Expectations Section */}
      <section className="py-16 bg-white dark:bg-lions-dark">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeIn} className="order-2 md:order-1">
              <h2 className="text-3xl font-bold mb-6 text-lions-blue dark:text-lions-yellow">
                Wat verwachten wij van onze leden?
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <FiCheck className="text-lions-blue dark:text-lions-yellow mt-1 mr-3 flex-shrink-0" />
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Betrokkenheid</strong> - Actieve deelname aan clubbijeenkomsten (tweemaal per maand) en projecten.
                  </p>
                </li>
                <li className="flex items-start">
                  <FiCheck className="text-lions-blue dark:text-lions-yellow mt-1 mr-3 flex-shrink-0" />
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Inzet</strong> - Bereidheid om tijd en expertise in te zetten voor het organiseren en uitvoeren van activiteiten.
                  </p>
                </li>
                <li className="flex items-start">
                  <FiCheck className="text-lions-blue dark:text-lions-yellow mt-1 mr-3 flex-shrink-0" />
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Contributie</strong> - Jaarlijkse bijdrage om de administratieve kosten te dekken (100% van de fondsenwerving gaat naar goede doelen).
                  </p>
                </li>
                <li className="flex items-start">
                  <FiCheck className="text-lions-blue dark:text-lions-yellow mt-1 mr-3 flex-shrink-0" />
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Ambassadeurschap</strong> - Het uitdragen van de Lions-waarden in uw persoonlijke en professionele netwerk.
                  </p>
                </li>
              </ul>
            </motion.div>
            <motion.div variants={fadeIn} className="order-1 md:order-2">
              <img 
                src={boekenmarktImage} 
                alt="Lions Club Boekenmarkt project" 
                className="rounded-lg shadow-lg w-full"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-lions-light dark:bg-gray-900">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl font-bold mb-4 text-lions-blue dark:text-lions-yellow"
            >
              Wat onze leden zeggen
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto"
            >
              Ontdek waarom onze leden zich hebben aangesloten bij Lions Club Hoorn De Doelen.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            <Testimonial 
              quote="Als Lion kan ik mijn professionele vaardigheden inzetten om echt verschil te maken in onze gemeenschap. De vriendschappen die ik heb opgebouwd zijn onbetaalbaar."
              name="Jan de Vries"
              role="Lid sinds 2010"
              image="/images/member1.jpg"
            />
            <Testimonial 
              quote="Het geeft enorm veel voldoening om samen met andere betrokken mensen projecten op te zetten die kwetsbare groepen in onze samenleving helpen."
              name="Peter Lenaers"
              role="Lid sinds 2015"
              image="/images/member2.jpg"
            />
            <Testimonial 
              quote="De combinatie van lokale betrokkenheid en internationale verbondenheid maakt het Lions-lidmaatschap voor mij bijzonder waardevol."
              name="Sjouke Bakker"
              role="Lid sinds 2018"
              image="/images/member3.jpg"
            />
          </motion.div>
        </div>
      </section>

      {/* Join Section */}
      <section id="aanmelden" className="py-16 bg-lions-blue dark:bg-lions-dark text-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl font-bold mb-6 text-white dark:text-lions-yellow"
            >
              Meer weten over Lions De Doelen Hoorn?
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-lg mb-8"
            >
              Wij verwelkomen nieuwe leden die onze passie voor dienstverlening delen. Vul het formulier in om meer informatie te ontvangen of om een vrijblijvende kennismaking aan te vragen.
            </motion.p>
            <motion.div variants={fadeIn}>
              <a 
                href="/contact" 
                className="btn-secondary bg-white text-lions-blue hover:bg-lions-yellow hover:text-white dark:bg-lions-yellow dark:text-lions-dark dark:hover:bg-white"
              >
                Neem contact op
              </a>
            </motion.div>
            <motion.p 
              variants={fadeIn}
              className="mt-6 text-sm opacity-80"
            >
              U kunt ook direct contact opnemen met onze secretaris via <a href="mailto:secretaris@mail.lionshoorn.nl" className="underline hover:text-lions-yellow">secretaris@mail.lionshoorn.nl</a> of <a href="tel:+31614267886" className="underline hover:text-lions-yellow">06-14267886</a>.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white dark:bg-lions-dark">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl font-bold mb-4 text-lions-blue dark:text-lions-yellow"
            >
              Veelgestelde vragen
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto"
            >
              Antwoorden op de meest gestelde vragen over lidmaatschap bij Lions Club Hoorn De Doelen.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-3xl mx-auto divide-y divide-gray-200 dark:divide-gray-700"
          >
            <motion.div variants={fadeIn} className="py-5">
              <h3 className="text-xl font-bold text-lions-blue dark:text-lions-yellow mb-2">Hoeveel tijd kost het lidmaatschap?</h3>
              <p className="text-gray-700 dark:text-gray-300">
                We komen tweemaal per maand samen voor vergaderingen en een gezamenlijk diner en borrel vooraf. Daarnaast organiseren we verschillende activiteiten en projecten waar leden aan kunnen deelnemen op basis van hun beschikbaarheid en interesses.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="py-5">
              <h3 className="text-xl font-bold text-lions-blue dark:text-lions-yellow mb-2">Wat zijn de kosten van het lidmaatschap?</h3>
              <p className="text-gray-700 dark:text-gray-300">
                De jaarlijkse contributie bedraagt €300. Dit dekt administratieve kosten, bijdragen aan Lions Clubs International en de kosten van onze bijeenkomsten. 100% van de fondsen die we werven gaan naar goede doelen.
                De kosten voor een diner en borrel worden per keer achteraf in rekening gebracht via je rekening courant met de club.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="py-5">
              <h3 className="text-xl font-bold text-lions-blue dark:text-lions-yellow mb-2">Hoe verloopt het aanmeldingsproces?</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Na uw aanmelding nodigen we u uit voor een kennismakingsgesprek. Vervolgens kunt u enkele bijeenkomsten bijwonen om de club beter te leren kennen. Als er wederzijdse interesse is, wordt u voorgedragen voor lidmaatschap.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="py-5">
              <h3 className="text-xl font-bold text-lions-blue dark:text-lions-yellow mb-2">Moet ik in Hoorn wonen om lid te worden?</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Niet noodzakelijk. Veel van onze leden wonen in (de buurt van) Hoorn, maar we verwelkomen ook leden uit omliggende gemeenten die zich betrokken voelen bij onze regio en projecten.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="py-5">
              <h3 className="text-xl font-bold text-lions-blue dark:text-lions-yellow mb-2">Zijn er leeftijdsvereisten voor lidmaatschap?</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Er is geen specifieke leeftijdsgrens. We verwelkomen leden van alle leeftijden die onze waarden delen en zich willen inzetten voor de gemeenschap. Gezien de leeftijd van onze leden nodigen wij vooral mensen van 45 - 70 jaar uit om lid te worden.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Membership; 